import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getModulesForUser } from "../../config/modulesConfig";
import { useLanguage } from "../../i18n/LanguageContext";
import "./StaffLanding.css";

const sections = [
  {
    id: "workspace",
    title: "Core Operations",
    description: "People, branches and daily union operations",
    modules: ["staff-profile", "dashboard", "members", "branches", "id-cards"],
  },
  {
    id: "finance",
    title: "Finance & Resources",
    description: "Income, expenditure, requests and financial reporting",
    modules: [
      "accounting",
      "contributions",
      "requisitions",
      "loans",
      "reports",
    ],
  },
  {
    id: "governance",
    title: "Governance & Communication",
    description: "Engagement, meetings, elections and recruitment",
    modules: ["communications", "engagement", "meetings", "trainings", "elections", "recruitment"],
  },
  {
    id: "administration",
    title: "System Administration",
    description: "User access and system configuration",
    modules: ["users", "card-templates", "settings"],
  },
];

const StaffLanding = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const accessibleModules = getModulesForUser(user);
  const groupedModules = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const accessible = new Map(
      accessibleModules.map((module) => [module.id, module]),
    );
    return sections
      .map((section) => ({
        ...section,
        items: section.modules
          .map((id) => accessible.get(id))
          .filter(Boolean)
          .filter(
            (module) =>
              !query ||
              module.name.toLowerCase().includes(query) ||
              module.description.toLowerCase().includes(query),
          ),
      }))
      .filter((section) => section.items.length > 0);
  }, [accessibleModules, searchQuery]);
  const displayName =
    user?.name?.trim() || user?.email?.split("@")[0] || t("Staff");

  return (
    <div className="staff-landing">
      <section className="landing-header">
        <div className="landing-header-content">
          <span className="landing-kicker">TAREWU DIGITAL WORKSPACE</span>
          <h1 className="landing-title">
            {t("Welcome,")} {displayName}
          </h1>
          <p className="landing-subtitle">
            Select a module to continue with your work.
          </p>
        </div>
        <div className="landing-summary">
          <div>
            <small>Access level</small>
            <strong>{user?.role?.replaceAll("_", " ")}</strong>
          </div>
          <div>
            <small>Available modules</small>
            <strong>{accessibleModules.length}</strong>
          </div>
        </div>
      </section>
      <div className="landing-toolbar">
        <div>
          <h2>Modules</h2>
          <p>Modules are organized by business function.</p>
        </div>
        <div className="modules-search">
          <svg
            className="search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder={t("Search modules...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {groupedModules.length > 0 ? (
        <div className="modules-container">
          {groupedModules.map((section, sectionIndex) => (
            <section key={section.id} className="modules-category">
              <div className="category-heading">
                <div className="category-number">
                  {String(sectionIndex + 1).padStart(2, "0")}
                </div>
                <div>
                  <h2>{t(section.title)}</h2>
                  <p>{t(section.description)}</p>
                </div>
                <span>
                  {section.items.length}{" "}
                  {section.items.length === 1 ? "module" : "modules"}
                </span>
              </div>
              <div className="modules-grid">
                {section.items.map((module) => (
                  <button
                    key={module.id}
                    className="module-card"
                    onClick={() => navigate(module.path)}
                    style={{ "--module-color": module.color }}
                  >
                    <div className="module-icon">{module.icon}</div>
                    <div className="module-content">
                      <h3>{t(module.name)}</h3>
                      <p>{t(module.description)}</p>
                    </div>
                    <div className="module-open" aria-hidden="true">
                      →
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="no-modules">
          <div className="no-modules-mark">0</div>
          <h2>{t("No modules found")}</h2>
          <p>
            {searchQuery
              ? `${t("No modules match")} "${searchQuery}"`
              : t("You do not have access to any modules yet")}
          </p>
          <button onClick={() => setSearchQuery("")}>Clear search</button>
        </div>
      )}
    </div>
  );
};
export default StaffLanding;
